import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Helmet } from '@/components/base/Helmet';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        params.append(key, value.toString());
      }
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u06378l97n3eas3epg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <Helmet
        title="关于我们 - 亚洲自拍完全指南"
        description="了解亚洲自拍完全指南的品牌故事、用户口碑与联系方式。我们致力于为亚洲自拍爱好者提供最专业、最实用的指南内容。"
        keywords="关于我们,亚洲自拍,自拍指南品牌,联系我们"
        canonical="https://ahpinxing.cn/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "亚洲自拍完全指南",
            "url": "https://ahpinxing.cn",
            "description": "探索亚洲自拍的完整世界——从专业拍摄技巧、热门滤镜风格，到最佳自拍设备推荐与构图秘诀。",
            "foundingDate": "2024",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "hello@ahpinxing.cn",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://ahpinxing.cn",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "关于我们",
              },
            ],
          }),
        }}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        <div className="pt-20 pb-8 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-100 mb-4">
                关于我们
              </h1>
              <p className="text-foreground-400 text-sm md:text-base max-w-2xl mx-auto">
                我们是一群热爱自拍的亚洲创作者，致力于为每一位自拍爱好者提供最专业、最实用的指南内容。
              </p>
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-100 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-400 leading-relaxed">
                  <p>
                    亚洲自拍完全指南成立于2024年，由一群热衷于自拍文化和摄影技术的亚洲创作者共同发起。我们发现在中文互联网上，关于亚洲自拍的系统性指南非常稀缺，大多数内容零散分布、缺乏深度。
                  </p>
                  <p>
                    因此，我们决定创建一个专注于亚洲自拍文化的专业内容平台。从拍摄技巧到设备推荐，从滤镜调色到美妆搭配，我们致力于覆盖自拍领域的每一个细节，为读者提供最全面、最实用的指南。
                  </p>
                  <p>
                    我们的团队成员包括专业摄影师、美妆博主、数码评测达人以及资深社交媒体运营专家，每一位成员都在自己的领域有着丰富的实践经验和独到的见解。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20team%20of%20Asian%20content%20creators%20collaborating%20in%20modern%20bright%20studio%2C%20warm%20natural%20light%2C%20minimal%20workspace%2C%20cream%20walls%2C%20plants%2C%20editorial%20lifestyle%20photography%2C%20friendly%20atmosphere%2C%20professional%20creative%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="亚洲自拍完全指南团队"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-100 mb-3 text-center">
              我们的使命
            </h2>
            <p className="text-foreground-400 text-sm md:text-base text-center max-w-2xl mx-auto mb-10">
              让每一位亚洲自拍爱好者都能拍出令自己满意的照片
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                  <i className="ri-book-open-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-100 mb-2">专业内容</h3>
                <p className="text-xs md:text-sm text-foreground-400">每一篇文章都由行业专家撰写与审核</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-3">
                  <i className="ri-group-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-100 mb-2">社群驱动</h3>
                <p className="text-xs md:text-sm text-foreground-400">倾听用户声音，持续优化内容方向</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mx-auto mb-3">
                  <i className="ri-lightbulb-flash-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-100 mb-2">持续创新</h3>
                <p className="text-xs md:text-sm text-foreground-400">紧跟潮流趋势，不断探索新领域</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-100 mb-3 text-center">
              用户口碑
            </h2>
            <p className="text-foreground-400 text-sm text-center max-w-2xl mx-auto mb-10">
              来自用户们的真实反馈，是我们前进的最大动力
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="p-5 rounded-lg bg-background-50">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-300 leading-relaxed mb-3">
                  "作为一个自拍小白，这个网站真的帮了我很多。构图技巧那一篇看完后，我的自拍水平明显提升了，朋友们都说变化很大！"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary-500">晓</span>
                  </div>
                  <span className="text-xs text-foreground-400">晓琳 · 大学生</span>
                </div>
              </div>
              <div className="p-5 rounded-lg bg-background-50">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-300 leading-relaxed mb-3">
                  "设备推荐那篇文章太实用了！按照推荐买了环形灯和手机支架，现在在家也能拍出专业级别的自拍照了。"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-accent-500">雅</span>
                  </div>
                  <span className="text-xs text-foreground-400">雅琪 · 上班族</span>
                </div>
              </div>
              <div className="p-5 rounded-lg bg-background-50">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-300 leading-relaxed mb-3">
                  "韩系滤镜那篇看完直接去下了Lightroom，按照参数调完真的太绝了！朋友圈点赞量翻了不止一倍！"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-secondary-500">诗</span>
                  </div>
                  <span className="text-xs text-foreground-400">诗涵 · 博主</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-100 mb-5">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed mb-6">
                  有任何问题、建议或合作意向？欢迎通过以下方式联系我们，我们会在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-100">电子邮箱</p>
                      <p className="text-sm text-foreground-400">hello@ahpinxing.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-100">微信公众号</p>
                      <p className="text-sm text-foreground-400">亚洲自拍指南</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-100">办公地址</p>
                      <p className="text-sm text-foreground-400">上海市徐汇区漕溪北路88号圣爱大厦1208室</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-lg bg-background-100 p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-4">
                    发送消息
                  </h3>
                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form="true"
                    id="contact-form"
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm text-foreground-300 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的姓名"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm text-foreground-300 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的邮箱"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm text-foreground-300 mb-1.5">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入你想说的话（最多500字）"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                    </div>
                    <div className="honeypot-wrapper">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-500 text-center">消息发送成功！感谢你的留言，我们会尽快回复。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}